/**
 * Nettoyage des deux seuls textes libres du site : le message du livre d'or, et
 * le pseudo kikoo qui le signe.
 *
 * React échappe déjà tout ce qu'il rend, donc l'injection HTML n'est pas le
 * sujet. Ce qui reste, et qui casse vraiment une page publique, c'est :
 *  - les caractères de contrôle bidirectionnels (RLO, LRE, isolats), qui
 *    permettent d'afficher un texte dans un ordre différent de son contenu
 *    réel — la famille « Trojan Source » ;
 *  - les caractères invisibles (largeur nulle, BOM) qui servent à faire passer
 *    des messages vides pour du contenu ;
 *  - les empilements de diacritiques (« Zalgo ») qui débordent des blocs voisins ;
 *  - les messages très longs, qui déforment la mise en page.
 *
 * Tout est ramené à une ligne simple, lisible, bornée.
 */

/** Longueur maximale d'un message, en points de code. */
export const MAX_ENTRY_LENGTH = 280;

/** Nombre maximal d'entrées conservées dans le livre d'or. */
export const MAX_ENTRIES = 60;

/** Diacritiques consécutifs tolérés avant qu'on parle d'empilement. */
const MAX_COMBINING_RUN = 2;

/**
 * Les caractères visés sont invisibles par nature : les écrire tels quels dans
 * le fichier rendrait la règle impossible à relire (et un `git diff` illisible).
 * Ils sont donc décrits par leurs points de code, puis assemblés en classes.
 */
type Range = readonly [start: number, end: number];

function charClass(ranges: readonly Range[]): RegExp {
  const body = ranges
    .map(([start, end]) => {
      const from = `\\u{${start.toString(16)}}`;
      const to = `\\u{${end.toString(16)}}`;
      return start === end ? from : `${from}-${to}`;
    })
    .join('');
  return new RegExp(`[${body}]`, 'gu');
}

/** Contrôles C0 et C1 — tabulation et sauts de ligne inclus : le champ est une ligne. */
const CONTROL = charClass([
  [0x00, 0x1f],
  [0x7f, 0x9f],
]);

/** Marques directionnelles, incorporations, surcharges et isolats (Trojan Source). */
const BIDI = charClass([
  [0x061c, 0x061c],
  [0x200e, 0x200f],
  [0x202a, 0x202e],
  [0x2066, 0x2069],
]);

/** Largeur nulle, liants, trait d'union conditionnel, marqueurs de formatage, BOM. */
const INVISIBLE = charClass([
  [0x00ad, 0x00ad],
  [0x200b, 0x200d],
  [0x2060, 0x2064],
  [0xfeff, 0xfeff],
]);

/** Espaces typographiques et idéographiques, ramenés à une espace normale. */
const ODD_SPACES = charClass([
  [0x00a0, 0x00a0],
  [0x1680, 0x1680],
  [0x2000, 0x200a],
  [0x202f, 0x202f],
  [0x205f, 0x205f],
  [0x3000, 0x3000],
]);

/** Marques combinantes (Mn/Me) — le drapeau `u` autorise les propriétés Unicode. */
const COMBINING = /^(?:\p{Mn}|\p{Me})$/u;

/** Limite les empilements de diacritiques sans toucher aux accents normaux. */
function clampCombiningRuns(input: string): string {
  let out = '';
  let run = 0;
  for (const char of input) {
    if (COMBINING.test(char)) {
      run += 1;
      if (run > MAX_COMBINING_RUN) continue;
    } else {
      run = 0;
    }
    out += char;
  }
  return out;
}

/** Tronque en points de code, sans couper une paire de substitution en deux. */
function truncate(input: string, max: number): string {
  const chars = Array.from(input);
  return chars.length <= max ? input : chars.slice(0, max).join('');
}

/**
 * Renvoie le message prêt à afficher, ou une chaîne vide si le message ne
 * contenait rien d'affichable (auquel cas l'appelant l'ignore).
 */
export function sanitizeEntryText(raw: string): string {
  if (typeof raw !== 'string') return '';

  let text = raw.normalize('NFC');
  text = text.replace(CONTROL, ' ');
  text = text.replace(BIDI, '');
  text = text.replace(INVISIBLE, '');
  text = text.replace(ODD_SPACES, ' ');
  text = clampCombiningRuns(text);
  text = text.replace(/\s+/gu, ' ').trim();

  return truncate(text, MAX_ENTRY_LENGTH);
}

/**
 * Longueur maximale d'un pseudo kikoo fini.
 *
 * Le pire cas du générateur tient dedans : 14 caractères de blaze, reflet
 * compris (29), plus le département (6), plus `xX_ _Xx` (6), plus les étoiles
 * (4) — 45. La borne existe quand même, parce que c'est elle qui garde la
 * valeur relue du stockage.
 */
export const MAX_KIKOO_LENGTH = 48;

/**
 * Nettoie un pseudo — celui que le générateur vient de produire, ou celui qui
 * sort de `localStorage`.
 *
 * Même famille de menaces que ci-dessus, avec une contrainte de plus : un pseudo
 * est **d'un seul tenant**. Toute espace disparaît au lieu d'être normalisée,
 * sinon une signature pourrait s'étaler sur la largeur d'une ligne de livre d'or.
 * Les étoiles, les idéogrammes de la guirlande et les tirets bas, eux, restent.
 */
export function sanitizeKikoo(raw: unknown): string {
  if (typeof raw !== 'string') return '';

  let text = raw.normalize('NFC');
  text = text.replace(CONTROL, '');
  text = text.replace(BIDI, '');
  text = text.replace(INVISIBLE, '');
  text = text.replace(ODD_SPACES, '');
  text = clampCombiningRuns(text);
  text = text.replace(/\s+/gu, '');

  return truncate(text, MAX_KIKOO_LENGTH);
}
